// Walkthroughs page — the pure parts (no React), unit-tested in the "model"
// block of __tests__/tourTests/walkthroughsPage.test.js. Registry §36.10.

export const TABS = [
  { key: "all", label: "All" },
  { key: "web", label: "Web" },
  { key: "mobile", label: "Mobile" },
  { key: "people", label: "People" },
];

export const PLATFORM_LABEL = { web: "Web", mobile: "Mobile" };

export const STATUS_LABEL = {
  completed: "Completed",
  started: "Started",
  dismissed: "Dismissed",
  not_seen: "Not seen",
};

/** The tours a platform tab shows (People has no tour rows). */
export function toursForTab(tours, tab) {
  const all = tours || [];
  if (tab === "web" || tab === "mobile") return all.filter((t) => t.platform === tab);
  return all;
}

/** "All (6)" style labels; People counts distinct people. */
export function tabCounts(tours) {
  const all = tours || [];
  return {
    all: all.length,
    web: all.filter((t) => t.platform === "web").length,
    mobile: all.filter((t) => t.platform === "mobile").length,
    people: peopleFromReport(all).length,
  };
}

/** A person can be assigned a tour they haven't completed and aren't already assigned. */
export const canAssign = (u) => !u.completedAt && !u.assigned;

/**
 * The People tab: one entry per active person across every tour they are
 * eligible for (the report lists them under each tour), with what the admin
 * needs at a glance.
 *
 *   due  — tours assigned to them that still count as new (the server's truth:
 *          an assignment is a to-do until completed; a preview or Help-only
 *          tour is never due)
 *   done — every eligible tour completed
 */
export function peopleFromReport(tours) {
  const byId = new Map();
  (tours || []).forEach((tour) => {
    (tour.users || []).forEach((u) => {
      const p = byId.get(u.userId) || { userId: u.userId, name: u.name, permission: u.permission, tours: [] };
      p.tours.push({
        tourId: tour.id,
        title: tour.title,
        platform: tour.platform,
        isPreview: tour.isPreview,
        status: u.status,
        isNew: u.isNew,
        assigned: u.assigned,
        assignedAt: u.assignedAt || null,
        completedAt: u.completedAt || null,
      });
      byId.set(u.userId, p);
    });
  });
  return [...byId.values()]
    .map((p) => ({
      ...p,
      due: p.tours.filter((t) => t.assigned && t.isNew).length,
      done: p.tours.length > 0 && p.tours.every((t) => !!t.completedAt),
    }))
    .sort((a, b) => b.due - a.due || a.name.localeCompare(b.name));
}

/**
 * One snack for a batch of per-person `assignTours` calls (`Promise.allSettled`
 * results, in `targets` order). Each call carries ONE tour, so a person's
 * result falls in exactly one bucket.
 */
export function assignSummary(targets, results) {
  let assigned = 0;
  let completed = 0;
  let rejected = 0;
  const failed = [];
  (results || []).forEach((r, i) => {
    const res = r.status === "fulfilled" ? r.value?.data?.assignTours : null;
    if (!res) {
      failed.push(targets[i]?.name || "someone");
      return;
    }
    if (res.assigned.length) assigned += 1;
    else if (res.skippedCompleted.length) completed += 1;
    else rejected += 1;
  });
  const parts = [];
  if (assigned) parts.push(`Assigned to ${assigned === 1 ? targets.find((_, i) => results[i]?.value?.data?.assignTours?.assigned?.length)?.name || "1" : assigned}`);
  if (completed) parts.push(`${completed} already completed`);
  if (rejected) parts.push(`${rejected} not eligible`);
  if (failed.length) parts.push(`failed: ${failed.join(", ")}`);
  return parts.join(" · ") || "Nothing to assign";
}

/** The message for a rejected mutation promise. */
export function mutationErrorText(err, fallback) {
  const msg = (err && err.message) || "";
  if (msg.includes("READ_ONLY_IMPERSONATION")) return "Can't change assignments while impersonating.";
  return fallback;
}
